import { Grid, IconButton } from '@mui/material';
import { memo, useMemo, useRef, useState } from 'react';
import { AccordionPracticeVM } from 'practice/view-models/practice-detail';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Edit } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useParams } from 'react-router-dom';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { TypeRegister } from 'practice/constants/type-register';
import { toast } from 'react-toastify';
import { TypeInitiative } from 'practice/constants/practice-management-step';

interface FormProps {
  disabled?: boolean;
  onChange: (
    key: keyof AccordionPracticeVM,
    value: AccordionPracticeVM[keyof AccordionPracticeVM]
  ) => void;
  values?: AccordionPracticeVM;
  withoutDateClose?: boolean;
  isDetail?: boolean;
}

const FormComponent = ({
  disabled,
  onChange,
  values,
  withoutDateClose = false,
  isDetail,
}: FormProps): React.ReactElement => {
  const nrPraticaAnpr = useMemo(() => {
    if (values?.tipoIniziativa === TypeInitiative.Anpr && values?.numberPracticeAnpr) {
      return values?.numberPracticeAnpr;
    } else if (
      values?.tipoIniziativa !== TypeInitiative.Anpr &&
      values?.tipoIniziativa !== TypeInitiative.ElixForm
    ) {
      return values?.typeInitiativeDescription || '';
    } else return null;
  }, [values]);

  const [enableEdit, setEnableEdit] = useState(false);

  const previousDate = useRef<DateTime | null>(values?.dateInception || null);

  const dateChange = useHttpClient(`${__PRACTICE_API_URL__}/rettifica-pratiche/cambio-data`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const urlparams = useParams();

  const checkPractice = useMemo(() => {
    return (
      (urlparams.tipoRegistro === TypeRegister.Immigration ||
        urlparams.tipoRegistro === TypeRegister.Emigration ||
        urlparams.tipoRegistro === TypeRegister.Residence ||
        urlparams.tipoRegistro === TypeRegister.RegisterOfficeTemporary ||
        urlparams.tipoRegistro === TypeRegister.Unavailable ||
        urlparams.tipoRegistro === TypeRegister.UntraceableCensus) &&
      !isDetail &&
      values?.codiceStatoPratica === '8'
    );
  }, [urlparams, isDetail, values?.codiceStatoPratica]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="NR pratica"
          aria-label="NR pratica"
          id="numberPractice"
          name="numberPractice"
          fullWidth
          type="text"
          disabled={disabled}
          variant="outlined"
          value={values?.numberPractice || ''}
          onChange={(event) => onChange('numberPractice', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Grid
          item
          xs={12}
          container
          spacing={0}
          columnSpacing={1}
          display={'flex'}
          alignItems={'center'}
        >
          <Grid item xs={12} md={enableEdit ? 7 : 10} lg={enableEdit ? 9 : 10}>
            <DatePicker
              label="Data decorrenza"
              aria-label="Data decorrenza"
              value={values?.dateInception || null}
              onChange={(date) => onChange('dateInception', date || null)}
              disabled={!enableEdit /*|| disabled */}
              renderInput={(params) => (
                <PmiTextField {...params} id="dateInception" fullWidth helperText="" />
              )}
            />
          </Grid>
          {!enableEdit ? (
            <Grid item xs={12} md={2}>
              <IconButton
                disabled={!checkPractice /*|| disabled */}
                onClick={() => {
                  setEnableEdit(true);
                }}
              >
                <Edit></Edit>
              </IconButton>
            </Grid>
          ) : (
            <Grid item xs={12} md={5} lg={3}>
              <span>
                <IconButton
                  onClick={() => {
                    (
                      dateChange({
                        data: {
                          key: {
                            codiceEnte: urlparams.codiceEnte,
                            tipoRegistro: urlparams.tipoRegistro,
                            idAnnoPratica:
                              urlparams.idAnnoPratica ??
                              Number.parseInt(urlparams.idAnnoPratica, 10),
                            progressivoPratica:
                              urlparams.progressivoPratica ??
                              Number.parseInt(urlparams.progressivoPratica, 10),
                          },
                          dataDecorrenza: values?.dateInception?.toFormat('yyyy-MM-dd'),
                        },
                      }) as ResponsePromise<void>
                    ).then(() => {
                      toast.success('Operazione eseguita con successo');
                      setEnableEdit(false);
                    });
                  }}
                >
                  <CheckCircleIcon></CheckCircleIcon>
                </IconButton>
                <IconButton
                  onClick={() => {
                    onChange('dateInception', previousDate.current);
                    setEnableEdit(false);
                  }}
                >
                  <HighlightOffIcon></HighlightOffIcon>
                </IconButton>
              </span>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Stato"
          aria-label="Stato"
          id="state"
          name="state"
          fullWidth
          type="text"
          disabled={disabled}
          variant="outlined"
          value={values?.state || ''}
          onChange={(event) => onChange('state', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Ultima operazione"
          aria-label="Ultima operazione"
          id="lastOperation"
          name="lastOperation"
          fullWidth
          type="text"
          disabled={disabled}
          variant="outlined"
          value={values?.lastOperation || ''}
          onChange={(event) => onChange('lastOperation', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <PmiTextField
          label="Tipo pratica"
          aria-label="Tipo pratica"
          id="typePractice"
          name="typePractice"
          fullWidth
          type="text"
          disabled={disabled}
          variant="outlined"
          value={values?.typePractice || ''}
          onChange={(event) => onChange('typePractice', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data apertura"
          aria-label="Data apertura"
          value={values?.dateOpen || null}
          onChange={(date) => onChange('dateOpen', date || null)}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField {...params} id="dateOpen" fullWidth helperText="" />
          )}
        />
      </Grid>
      {withoutDateClose ? undefined : (
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data chiusura"
            aria-label="Data chiusura"
            value={values?.dateClose || null}
            onChange={(date) => onChange('dateClose', date || null)}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField {...params} id="dateClose" fullWidth helperText="" />
            )}
          />
        </Grid>
      )}
      {nrPraticaAnpr ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            label={
              values?.tipoIniziativa !== TypeInitiative.Anpr &&
              values?.tipoIniziativa !== TypeInitiative.ElixForm
                ? 'Tipo iniziativa'
                : 'NR pratica ANPR'
            }
            aria-label={
              values?.tipoIniziativa !== TypeInitiative.Anpr &&
              values?.tipoIniziativa !== TypeInitiative.ElixForm
                ? 'Tipo iniziativa'
                : 'NR pratica ANPR'
            }
            id="idAnpr"
            name="idAnpr"
            fullWidth
            type="text"
            disabled
            variant="outlined"
            value={nrPraticaAnpr || ''}
          />
        </Grid>
      ) : null}
      {values?.state === 'Irricevibile' ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="Motivo di irricevibilità"
            aria-label="Motivo di irricevibilità"
            id="reasonForPraticeNotAvailable"
            name="reasonForPraticeNotAvailable"
            fullWidth
            type="text"
            disabled={disabled}
            variant="outlined"
            value={values?.reasonForPraticeNotAvailable || ''}
            onChange={(event) => onChange('reasonForPraticeNotAvailable', event.target.value)}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormComponent.displayName = 'Practice Detail - Form - accordion practice - form';

const Form = memo(FormComponent);

export default Form;
